import { type Form as FormEntity } from '@wix/ambassador-forms-v4-form/types';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';
import {
  type FormView,
  type Breakpoint,
  DefaultFieldLayout,
  createFormFields,
  getSortedFields,
  useFocusedField,
  type FieldActions,
  type FieldActionsContextValue,
  type FormFields,
  type InputElement,
} from '@wix/form-fields';
import { type ValidationError, type ValidationMode } from '@wix/form-validator';
import { deepEqual } from 'fast-equals';
import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  type ForwardedRef,
} from 'react';

import { applyAllOverrides } from '../components/form/all-overrides';
import { clearFieldsWhenHiddenByRule } from '../components/form/clear-fields';
import { getStepIndex } from '../components/form/get-step-index';
import {
  type FormActions,
  type FormConfig,
  type FormOverrides,
  type FormHandle,
  type FormValues,
  type OnChange,
  type OnBlur,
  type OnFocus,
  type OnStepIdChange,
  type PossibleValues,
} from '../types';

import { fillEmptyValues } from './form-values';
import { useFormValues } from './use-form-values';
import { useFormView } from './use-form-view';
import { useReportFieldEdit } from './use-report-field-edit';
import { useScrollToFirstError } from './use-scroll-to-first-error';
import { useSubmit } from './use-submit';
import { type HandleFieldValidation, useValidation } from './use-validation';

type UseFormParams = {
  form: FormEntity;
  values: FormValues;
  stepId?: string;
  onStepIdChange?: OnStepIdChange;
  onChange: OnChange;
  onBlur?: OnBlur;
  onFocus?: OnFocus;
  errors: ValidationError[];
  onValidate: (errors: ValidationError[]) => void;
  onSubmit?: () => void;
  onSubmitSuccess?: (submission: FormSubmission) => void;
  onSubmitFailure?: (error: unknown) => void;
  enableScrollToFirstError?: boolean;
  forwardedRef?: ForwardedRef<FormHandle>;
  overrides?: FormOverrides;
  actions?: FormActions;
  config?: FormConfig | null;
  breakpoint?: Breakpoint;
  validationMode?: ValidationMode;
  fieldMap: FormFields;
  inProgress?: boolean;
  enableAutofill?: boolean;
};

type UseFormReturn = {
  currentView: FormView;
  formViews: FormView[];
  normalizedValues: FormValues;
  onFieldChange: (target: string, newValue: PossibleValues) => void;
  errors: ValidationError[];
  formFields: InputElement[];
  handleFieldValidation: HandleFieldValidation;
  formWithOverrides: FormEntity;
  currentStep: number;
  fieldActions: FieldActionsContextValue<FieldActions>;
  submitForm: () => Promise<FormSubmission | undefined>;
};

export const useForm = ({
  form: paramForm,
  values: paramValues,
  stepId,
  onStepIdChange,
  onChange,
  onBlur,
  onFocus,
  errors,
  onValidate,
  onSubmit,
  onSubmitSuccess,
  onSubmitFailure,
  enableScrollToFirstError = true,
  forwardedRef,
  overrides,
  actions,
  config,
  breakpoint,
  validationMode,
  fieldMap,
  inProgress,
  enableAutofill,
}: UseFormParams): UseFormReturn => {
  // `values` – we are working with the full list for all steps.
  // `errors` – working only with the errors list for the fields inside current step.
  const formWithOverrides = useMemo(
    () => applyAllOverrides(paramForm, paramValues, overrides),
    [paramForm, paramValues, overrides],
  );
  const formId = formWithOverrides.id!;
  const { setFocusedFieldId } = useFocusedField();
  const normalizedValues = useFormValues(
    formWithOverrides,
    paramValues,
    onChange,
    config,
    enableAutofill,
  );

  const valuesRef = useRef(normalizedValues);
  valuesRef.current = normalizedValues;

  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  const reportFieldEdit = useReportFieldEdit(formWithOverrides);
  const onFieldChange = (target: string, newValue: PossibleValues) => {
    const oldValues = valuesRef.current;
    const oldErrors = errorsRef.current;

    if (!deepEqual(newValue, oldValues[target])) {
      reportFieldEdit({
        [target]: newValue,
      });

      const newValues = {
        ...oldValues,
        [target]: newValue,
      };

      const cleared = clearFieldsWhenHiddenByRule(
        paramForm,
        overrides,
        oldValues,
        newValues,
        oldErrors,
      );

      onChange(cleared.values);
      onValidate(cleared.errors);
    }
  };
  const _onValidate = (newErrors: ValidationError[]) => {
    onValidate(newErrors);
  };

  const formViews = useFormView(formWithOverrides, stepId, breakpoint);
  const currentView = formViews[0];

  const { validateFields, validateStep, handleFieldValidation } = useValidation(
    {
      form: formWithOverrides,
      errorsRef,
      setErrors: _onValidate,
      validationMode,
    },
  );

  const handleStepChange = useCallback(
    (step: number) => {
      const newStepId = formWithOverrides.steps?.[step]?.id;

      if (newStepId) {
        const firstFieldInStep = getSortedFields(formWithOverrides, {
          stepId: newStepId,
        })[0];

        onStepIdChange?.(newStepId);
        setFocusedFieldId(firstFieldInStep?.id);
      }
    },
    [formWithOverrides, onStepIdChange, setFocusedFieldId],
  );

  const { scrollToFirstError, handleScrollToFirstError } =
    useScrollToFirstError({
      form: formWithOverrides,
      handleStepChange,
      currentStepId: stepId,
    });

  const resetFormValues = useCallback(() => {
    onChange(fillEmptyValues(formWithOverrides, {}));
  }, [formWithOverrides, onChange]);

  const validateAndScrollToError = useCallback(
    async (values: FormValues) => {
      const validationErrors = await validateFields(values);

      if (validationErrors && validationErrors.length) {
        handleScrollToFirstError(validationErrors);
      }

      return validationErrors;
    },
    [validateFields, handleScrollToFirstError],
  );

  const submitAction = useCallback(
    async (values: FormValues) => {
      const response = await actions?.submitForm?.(formId, values);
      return response?.submission;
    },
    [actions, formId],
  );

  const { submitForm } = useSubmit({
    submitAction,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    resetFormValues,
    validateAndScrollToError,
    values: normalizedValues,
    submitSettings: formWithOverrides.submitSettings,
    navigateToCheckout: actions?.navigateToCheckout,
    openPopup: actions?.openPopup,
    handleStepChange,
    onValidate: _onValidate,
    handleScrollToFirstError,
  });

  useImperativeHandle(
    forwardedRef,
    () => ({
      submit: submitForm,
      validate: async () => {
        const validationErrors = await validateFields(normalizedValues);

        if (validationErrors && validationErrors.length > 0) {
          if (enableScrollToFirstError) {
            scrollToFirstError(validationErrors);
          }
          return false;
        }
        return true;
      },
      validateStep: (step: string) => validateStep(step, normalizedValues),
      getValues: () => normalizedValues,
    }),
    [
      submitForm,
      validateStep,
      validateFields,
      normalizedValues,
      enableScrollToFirstError,
      scrollToFirstError,
    ],
  );

  const currentStep = getStepIndex(stepId, formWithOverrides.steps);

  const setNextStep = useCallback(
    () => handleStepChange(currentStep + 1),
    [currentStep, handleStepChange],
  );
  const setPreviousStep = useCallback(
    () => handleStepChange(currentStep - 1),
    [currentStep, handleStepChange],
  );

  const fieldActions = useMemo(
    () =>
      actions
        ? {
            actions: { ...actions, submitForm, setNextStep, setPreviousStep },
            formId,
            inProgress,
          }
        : { actions: { submitForm, setNextStep, setPreviousStep } },
    [actions, formId, inProgress, submitForm, setNextStep, setPreviousStep],
  );

  const formFields = createFormFields({
    form: currentView,
    formSchema: formWithOverrides,
    values: normalizedValues,
    onFieldChange,
    onBlur,
    onFocus,
    errors,
    FieldLayout: DefaultFieldLayout,
    fields: fieldMap,
    validateFields: handleFieldValidation,
    fieldActions,
  });

  return {
    currentView,
    formViews,
    normalizedValues,
    onFieldChange,
    errors,
    formFields,
    handleFieldValidation,
    formWithOverrides,
    currentStep,
    fieldActions,
    submitForm,
  };
};
